import React from 'react';
import { PMPFormPanel, syncFormState } from 'src/pages/pmp/nd/utils';
import NDEquipmentPanel from '../NDEquipmentPanel';
import NDAntennaPanel from '../NDAntennaPanel';
import {
  antennaHeightND,
  antennaSelection,
  cableLoss,
  framePeriod,
  smRegistrationLimit,
  smRangeFields,
  smReceiveTargetLevel,
  ndInterference,
  smDefaultInterference,
  bandwidth,
  modeledBeamwidth,
  azimuth,
  tilt,
  isPMP450m,
  eirpRowConfig,
  getNDUserPower,
  getUserEIRP,
  isPMP450V4x4,
  POWER_PANEL_FIELDS_WIDTH,
  getSmReceiveTargetLevel,
} from './config-utils';
import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import NDSectorPanel from '../NDSectorPanel';
import NDPowerPanel from '../NDPowerPanel';
import NDComponentCarriersPanel from '../NDComponentCarriersPanel';
import {
  getEIRPConfig,
  gasGroup,
  getPowerConfig,
  isPMP450v,
  regulation,
  band,
  product,
} from '../../config-utils';
import { isNumber } from 'lodash';

const equipment: PMPFormPanel = {
  kind: 'equipment',
  title: 'Equipment',
  name: 'nd_equipment',
  component: NDEquipmentPanel,
  fields: [
    {
      ...band,
      refreshesChoices: true,
      label({ formGetter }) {
        const carrierQty = formGetter('radios.0.equipment.carrier_qty');
        return carrierQty === 2 ? 'Band - Carrier 1' : 'Band';
      },
      afterOnChange(newValue, { setValue, getValues }, newChoices) {
        const [carrierQty, componentCarrierQty, radio1] = getValues([
          'radios.0.equipment.carrier_qty',
          'radio_qty',
          'radios.1',
        ]);
        if (carrierQty === 1 && componentCarrierQty === 2 && radio1) {
          setValue('radios.1.equipment.band', newValue);
        }
        // Sync CC1 (radios.0) to CC3 (radios.2) - always in same band/carrier
        if (carrierQty === 2 && componentCarrierQty === 3) {
          setValue('radios.2.equipment.band', newValue);
        }
        // Call the base band afterOnChange for noise density updates
        if (band.afterOnChange) {
          band.afterOnChange(newValue, { setValue, getValues }, newChoices);
        }
      },
    },
    {
      ...product,
      afterOnChange(newValue, { setValue, getValues }, newChoices) {
        const [carrierQty, componentCarrierQty] = getValues([
          'radios.0.equipment.carrier_qty',
          'radio_qty',
        ]);
        // Sync CC1 (radios.0) to CC3 (radios.2) - always in same carrier
        if (carrierQty === 2 && componentCarrierQty === 3) {
          setValue('radios.2.equipment.product', newValue);
        }
      },
    },
    {
      ...regulation,
      afterOnChange(newValue, { setValue, getValues }, newChoices) {
        const [carrierQty, componentCarrierQty] = getValues([
          'radios.0.equipment.carrier_qty',
          'radio_qty',
        ]);
        // Sync CC1 (radios.0) to CC3 (radios.2) - always in same carrier
        if (carrierQty === 2 && componentCarrierQty === 3) {
          setValue('radios.2.equipment.regulation', newValue);
        }
      },
    },
    {
      attrName: 'sync_input',
      getter: 'radios.0.equipment.sync_input',
    },
    {
      attrName: 'aes_encryption',
      getter: 'radios.0.equipment.aes_encryption',
    },
    {
      attrName: 'operating_mode',
      getter: 'radios.0.equipment.operating_mode',
    },
    {
      attrName: 'carrier_qty',
      getter: 'radios.0.equipment.carrier_qty',
      label: 'Number of Carriers',
      refreshesChoices: true,
      afterOnChange(newValue, formMethods, newChoices) {
        const { setValue, getValues } = formMethods;
        const { equipment } = newChoices;
        if (newValue == 2) {
          const radioQty = getValues('radio_qty');
          if (radioQty < 2) {
            // We have to have at least 2 radios when there are 2 carriers
            setValue('radio_qty', 2);
          }
          setValue('radios.1.equipment.band', equipment.band_carrier_2);
        } else {
          setValue('radio_qty', 1);
        }
      },
      show({ formGetter }) {
        return isPMP450V4x4(formGetter);
      },
    },
    {
      attrName: 'band_carrier_2',
      getter: 'radios.0.equipment.band_carrier_2',
      refreshesChoices: true,
      show({ formGetter }) {
        const carrierQty = formGetter('radios.0.equipment.carrier_qty');
        return carrierQty === 2;
      },
    },
  ],
};

const productConfig: PMPFormPanel = {
  kind: 'product',
  title: 'Product Configuration',
  name: 'nd_Product',
  component: NDEquipmentPanel,
  fields: [
    bandwidth,
    {
      attrName: 'color_code',
      getter: 'radios.0.equipment.color_code',
    },
    {
      attrName: 'adjacent_channel_support',
      getter: 'radios.0.equipment.adjacent_channel_support',
    },
    ...smRangeFields,
    framePeriod,
    smRegistrationLimit,
    {
      attrName: 'downlink_data',
      getter: 'radios.0.equipment.downlink_data',
    },
    {
      attrName: 'control_slots',
      getter: 'radios.0.equipment.control_slots',
    },
    {
      attrName: 'effective_control_slots',
      getter: 'radios.0.equipment.effective_control_slots',
    },
    {
      attrName: 'broadcast_repeat_count',
      getter: 'radios.0.equipment.broadcast_repeat_count',
    },
    {
      attrName: 'total_virtual_circuits',
      label: 'Total Virtual Circuits',
      getter: 'radios.0.equipment.total_virtual_circuits',

      component: GenericScaledField,
      componentProps() {
        return {};
      },
      editable: false,
    },
  ],
};

const antenna: PMPFormPanel = {
  kind: 'antenna',
  title: 'Antenna Configuration',
  name: 'nd_antenna',
  component: NDAntennaPanel,
  fields: [
    antennaSelection,
    modeledBeamwidth,
    antennaHeightND,
    azimuth,
    tilt,
    cableLoss,
  ],
};

const isPMP450VTwoCarrier = (formGetter): boolean => {
  const product = formGetter('radios.0.equipment.product');
  const carrierQty = formGetter('radios.0.equipment.carrier_qty');
  if (isPMP450v(product) && carrierQty >= 2) {
    return true;
  } else {
    return false;
  }
};

// Determines if power values should sync between CC1 and CC2
const shouldSyncAPPower = (formGetter) => {
  const product = formGetter('radios.0.equipment.product');
  const radio_qty = formGetter('radio_qty');
  if (isPMP450v(product) && radio_qty >= 2) {
    const band1 = formGetter('radios.0.equipment.band');
    const band2 = formGetter('radios.0.equipment.band_carrier_2');
    // Only sync when bands are actually the same
    return !band2 || band2 == band1;
  }
  return false;
};

// Determines when to show the info message
const getPMP450vAPPowerInfo = (formGetter, radioIdx) => {
  let message = null;

  const product = formGetter('radios.0.equipment.product');
  const radio_qty = formGetter('radio_qty');
  if (isPMP450v(product) && radio_qty >= 2) {
    const band1 = formGetter('radios.0.equipment.band');
    const band2 = formGetter('radios.0.equipment.band_carrier_2');
    if (!band2 || band2 == band1 || (radio_qty === 3 && radioIdx === 0)) {
      message = `
      Tx power is equally distributed between the CCs when
      the carriers are in the same band.
    `;
    }
  }

  return message;
};

const power: PMPFormPanel = {
  kind: 'power',
  title: 'Power',
  name: 'nd_power',
  show({ formGetter }) {
    return !isPMP450VTwoCarrier(formGetter);
  },
  component: NDPowerPanel,
  fields: [
    gasGroup,
    getEIRPConfig(),
    getUserEIRP(false),
    {
      ...getPowerConfig(),
      show({ formGetter }) {
        return !isPMP450m(formGetter);
      },
    },
    {
      ...getNDUserPower(false),
      info: ({ formGetter }) => {
        return getPMP450vAPPowerInfo(formGetter);
      },
      infoColor: 'yellow',
    },
    {
      ...smReceiveTargetLevel,
      afterOnChange(newValue, { getValues, setValue }, newChoices) {
        const carrierQty = getValues('radios.0.equipment.carrier_qty');
        if (carrierQty == 2) {
          setValue('radios.1.power.receive_target_level', newValue);
        }
      },
    },
    ndInterference,
    smDefaultInterference,
  ],
};

const pmp450VPower: PMPFormPanel = {
  kind: 'power',
  title: 'Power',
  name: 'nd_power',
  show({ formGetter }) {
    return isPMP450VTwoCarrier(formGetter);
  },
  component: NDSectorPanel,
  customClass: 'cnwave-power',
  sectorTitles({ formGetter }) {
    const radio_qty = formGetter('radio_qty');
    if (radio_qty === 3) {
      return [
        'Component Carrier 1/\nComponent Carrier 3',
        'Component Carrier 2',
      ];
    } else {
      return ['Component Carrier 1', 'Component Carrier 2'];
    }
  },
  fieldLayout: [
    [0, 1],
    [2, 3],
    [4, 5],
    [6, 7],
  ],
  fields: [
    ...eirpRowConfig,
    getPowerConfig(true),
    getPowerConfig(true, 1),
    {
      ...getNDUserPower(true),
      info: ({ formGetter }) => {
        return getPMP450vAPPowerInfo(formGetter, 0);
      },
      infoColor: 'yellow',
      afterOnChange(newValue, { setValue, getValues }, newChoices) {
        const shouldSync = shouldSyncAPPower(getValues);
        const userPower = getValues(`radios.0.power.user_power`);
        const radio_qty = getValues('radio_qty');
        if (isNumber(userPower)) {
          // Sync CC2 only when bands match
          if (shouldSync) {
            setValue('radios.1.power.use_user_power', true, {
              shouldValidate: true,
            });
            setValue('radios.1.power.user_power', userPower, {
              shouldValidate: true,
            });
          }
          // always sync with Component Carrier 3 (radios.2) if it exists
          if (radio_qty === 3) {
            setValue('radios.2.power.use_user_power', true, {
              shouldValidate: true,
            });
            setValue('radios.2.power.user_power', userPower, {
              shouldValidate: true,
            });
          }
        }
      },
      checkboxCompProps({ formGetter, formSetter }) {
        const shouldSync = shouldSyncAPPower(formGetter);
        const checked = formGetter(`radios.0.power.use_user_power`);
        const radio_qty = formGetter('radio_qty');
        // Sync CC2 only when bands match
        if (shouldSync) {
          formSetter('radios.1.power.use_user_power', checked);
        }
        // always sync with Component Carrier 3 (radios.2) if it exists
        if (radio_qty === 3) {
          formSetter('radios.2.power.use_user_power', checked);
        }
      },
    },
    {
      ...getNDUserPower(true, 1),
      info: ({ formGetter }) => {
        return getPMP450vAPPowerInfo(formGetter, 1);
      },
      infoColor: 'yellow',
      editable: ({ formGetter }) => {
        const shouldSync = shouldSyncAPPower(formGetter);
        // Prevent editing when values need to sync (bands match)
        return !shouldSync;
      },
    },
    {
      ...getSmReceiveTargetLevel(true, 0),
      label({ formGetter }) {
        const infoMsg = getPMP450vAPPowerInfo(formGetter, 1);
        if (infoMsg) {
          return 'SM Receive Target Level (Carrier)';
        } else {
          return 'SM Receive Target Level';
        }
      },
      afterOnChange(newValue, { getValues, setValue }, newChoices) {
        const infoMsg = getPMP450vAPPowerInfo(getValues, 1);
        // when we show the information then the
        // values need to sync
        if (infoMsg) {
          setValue('radios.1.power.receive_target_level', newValue);
        }
      },
    },
    {
      ...getSmReceiveTargetLevel(true, 1),
      label({ formGetter }) {
        const infoMsg = getPMP450vAPPowerInfo(formGetter, 1);
        if (infoMsg) {
          return 'SM Receive Target Level (Carrier)';
        } else {
          return 'SM Receive Target Level';
        }
      },
      editable: ({ formGetter }) => {
        const infoMsg = getPMP450vAPPowerInfo(formGetter, 1);
        // when we show the information then the
        // values need to sync, so prevent this from being editable
        return !infoMsg;
      },
    },
  ],
};

const componentCarrier: PMPFormPanel = {
  title: 'Component Carriers',
  kind: 'component_carrier',
  name: 'nd_component_carrier',
  component: NDComponentCarriersPanel,
  // Fields are derived dynamically in NDComponentCarrierPanel
  fields: [],
};

export const pmp450ViewConfig = {
  syncFormState,
  equipment,
  product: productConfig,
  antenna,
  power,
  sectorPower: pmp450VPower,
  componentCarrier,
};
